import { useState, type FC } from "react";
import { useAddMembers } from "../../../graphql/hooks/rolesAndPermissions/useAddMembers";
import Alert from "../../molecules/Alert";
import TextField from "../../atoms/inputs/TextField";
import { useUsers } from "../../../graphql/hooks/rolesAndPermissions/useUsers";
import TableRow from "../../atoms/TableRow";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { Col, Row } from "react-grid-system";
import ChipTag from "../../atoms/ChipTag";
import Checkbox from "../../atoms/inputs/Checkbox";
import TableHeader from "../../atoms/TableHeader";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { AddIcon } from "../../atoms/icons/AddIcon";
import { theme } from "../../../theme";
import PaginationComponent from "../../molecules/PaginationComponent";
import LoadingSpinner from "../../atoms/LoadingSpinner";

const PAGE_SIZE = 10;

interface Props {
  groupId: string;
  onSuccess?: () => void;
}

const AddMemberComponent: FC<Props> = ({ groupId, onSuccess }) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [searchString, setSearchString] = useState<string>("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);

  const onCompletedAddMember = (res?: any) => {
    if (res?.rolesAndPermissions?.addMembers?.success) {
      setErrorMessage(undefined);
      onSuccess && onSuccess();
    } else {
      setErrorMessage("Failed to add member to the group.");
    }
  };

  const onErrorAddMember = (e?: any) => {
    setErrorMessage(e?.message ?? "An error occurred while adding the member.");
  };

  const { addMembers, loading } = useAddMembers({
    onCompleted: onCompletedAddMember,
    onError: onErrorAddMember,
  });

  const handleAddMembers = async () => {
    try {
      await addMembers({
        variables: {
          input: {
            groupId: groupId,
            skynetUserIds: selectedUserIds,
          },
        },
      });
    } catch (error) {
      setErrorMessage(
        error?.toString() || "An error occurred while adding the member.",
      );
    }
  };

  const { skynetUsers, loading: usersLoading } = useUsers(
    searchString,
    undefined,
    undefined,
    "FIRST_NAME",
    "ASC",
    currentPage * PAGE_SIZE,
    PAGE_SIZE,
  );

  const numberOfPages = Math.ceil((skynetUsers?.totalCount ?? 0) / PAGE_SIZE);

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  return (
    <div>
      {usersLoading && !skynetUsers && <LoadingSpinner />}
      {errorMessage && (
        <Alert
          type="error"
          title="Kund einte lägga till medlemmar i grupp"
          message={errorMessage}
        />
      )}
      <Row nogutter justify="between" align="center">
        <TextField
          placeholder="Sök användare"
          onChange={(value) => {
            setSearchString(value);
            setCurrentPage(0);
          }}
          value={searchString}
        />
        <OutlinedButton
          onClick={handleAddMembers}
          disabled={selectedUserIds.length === 0}
          title={`Lägg till medlemmar (${selectedUserIds.length}st)`}
          medium
          icon={<AddIcon />}
          color={theme.colors.primary}
          loading={loading}
        />
      </Row>
      <HorizontalSpacer spacing={1} />
      <TableHeader>
        <Col lg={4}>
          <TableHeaderText>Namn</TableHeaderText>
        </Col>
        <Col lg={6}>
          <TableHeaderText>Grupper</TableHeaderText>
        </Col>
        <Col lg={2} style={{ display: "flex", justifyContent: "center" }}>
          <TableHeaderText>Lägg till i grupp</TableHeaderText>
        </Col>
      </TableHeader>
      {skynetUsers?.items?.map((user) => (
        <TableRow key={user.id} onClick={() => toggleUser(user.id)}>
          <Col lg={4}>
            <BodySmall>
              {user.firstName} {user.lastName}
            </BodySmall>
          </Col>
          <Col lg={6} style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {user.groups?.map((group) => (
              <ChipTag
                key={group?.groupId}
                tagTitleKey={
                  group?.isGroupAdmin
                    ? ` ${group?.groupName} - Gruppadmin`
                    : (group?.groupName ?? "")
                }
              />
            ))}
          </Col>
          <Col lg={2} style={{ display: "flex", justifyContent: "center" }}>
            <Checkbox
              checked={
                (user.groups?.some((group) => group?.groupId === groupId) ??
                  false) ||
                selectedUserIds.includes(user.id)
              }
              disabled={
                user.groups?.some((group) => group?.groupId === groupId) ??
                false
              }
              onChange={() => toggleUser(user.id)}
            />
          </Col>
        </TableRow>
      ))}
      <PaginationComponent
        numberOfPages={numberOfPages}
        currentPageIndex={currentPage}
        setCurrentPageIndex={setCurrentPage}
        spacing={1}
      />
    </div>
  );
};

export default AddMemberComponent;
